import { useEffect, useRef, useState } from "react";
import { Pause, Play, Square, Volume2 } from "lucide-react";
import { speakChunks } from "@/lib/studentSpeak";

type ListenState = "idle" | "playing" | "paused";

function pickVoice() {
  const voices = window.speechSynthesis?.getVoices() || [];
  return (
    voices.find((voice) => voice.lang === "en-US" && voice.localService) ||
    voices.find((voice) => voice.lang.startsWith("en") && voice.localService) ||
    voices.find((voice) => voice.lang.startsWith("en")) ||
    null
  );
}

/** Browser reads the activity text. No AWS. Hidden when there is nothing to say. */
export default function StudentListen({ text }: { text?: string | null }) {
  const script = String(text || "").trim();
  const [state, setState] = useState<ListenState>("idle");
  const [supported] = useState(() => typeof window !== "undefined" && "speechSynthesis" in window);
  const indexRef = useRef(0);
  const chunksRef = useRef<string[]>([]);
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null);

  const stop = () => {
    indexRef.current = 0;
    chunksRef.current = [];
    window.speechSynthesis?.cancel();
    setState("idle");
  };

  const speakFrom = (start: number) => {
    const synth = window.speechSynthesis;
    const chunks = chunksRef.current;
    if (!synth || start >= chunks.length) {
      stop();
      return;
    }
    const utterance = new SpeechSynthesisUtterance(chunks[start]);
    utterance.rate = 1;
    utterance.pitch = 1;
    if (voiceRef.current) utterance.voice = voiceRef.current;
    utterance.onend = () => {
      if (indexRef.current !== start) return;
      indexRef.current = start + 1;
      speakFrom(start + 1);
    };
    utterance.onerror = () => stop();
    synth.speak(utterance);
    setState("playing");
  };

  const start = () => {
    const synth = window.speechSynthesis;
    if (!synth || !script) return;
    synth.cancel();
    voiceRef.current = pickVoice();
    chunksRef.current = speakChunks(script);
    indexRef.current = 0;
    if (!chunksRef.current.length) return;
    speakFrom(0);
  };

  const pause = () => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.pause();
    setState(synth.paused ? "paused" : "idle");
    if (!synth.paused && !synth.speaking) stop();
  };

  const resume = () => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.resume();
    if (synth.speaking) setState("playing");
    else start();
  };

  useEffect(() => {
    const onVoices = () => {
      voiceRef.current = pickVoice();
    };
    window.speechSynthesis?.addEventListener("voiceschanged", onVoices);
    return () => {
      window.speechSynthesis?.removeEventListener("voiceschanged", onVoices);
      window.speechSynthesis?.cancel();
    };
  }, []);

  useEffect(() => {
    stop();
    // Restart only when they click again on the new page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [script]);

  if (!script || !supported) return null;

  return (
    <div className="student-listen">
      {state === "idle" ? (
        <button type="button" className="student-text-link" onClick={start}>
          <Volume2 size={14} aria-hidden />
          Listen
        </button>
      ) : (
        <>
          {state === "paused" ? (
            <button type="button" className="student-text-link" onClick={resume}>
              <Play size={14} aria-hidden />
              Resume
            </button>
          ) : (
            <button type="button" className="student-text-link" onClick={pause}>
              <Pause size={14} aria-hidden />
              Pause
            </button>
          )}
          <button type="button" className="student-text-link" onClick={stop}>
            <Square size={12} aria-hidden />
            Stop
          </button>
        </>
      )}
    </div>
  );
}
